/* eslint-disable func-names */
import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import groupBy from 'lodash/groupBy';

import WorkOrderComponent from './WorkOrderComponent';
import AcceptedJobModal from './JobDetails/AcceptedJobModal';
import RejectedJobModal from './JobDetails/RejectedJobModal';
import CreateJobModal from './CreateJobModal';
import LoaderComponent from '../../Shared/Loader';
import { AuthContext } from '../../Providers/UserProvider';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { MISSING_COMPANY_CLAIM_ERROR, companyIdFromClaims } from '../../utils/companyClaim';
import StatusEmptyCard from '../StatusPage/StatusEmptyCard';
import CreateSotSwitcher from './CreateSotSwitcher';
import JobsSotSwitcher from './JobsSotSwitcher';
import TrueEmptySwitcher from './TrueEmptySwitcher';
import {
    pwaCreateFixtureKind,
    pwaCreateFixtureNotice,
    pwaCreatePreviewSurface,
    pwaCreateSotActive,
    pwaTrueEmptyFixtureNotice,
    pwaTrueEmptyPreviewSurface,
    pwaTrueEmptySotActive,
    pwaTrueEmptyWalkKind,
} from './createPwaJob';
import {
    ORDERS_TAB_EMPTY,
    compareOrdersJobsByDate,
    ordersEmptyCopyKind,
    ordersFilterEmptyCard,
    ordersHeaderDetail,
    ordersJobDateKey,
    ordersJobMatchesFilter,
    ordersListCopy,
    isPwaWalkSot,
    pwaDisplayRegionId,
    pwaJobsFixtureError,
    pwaLiveWriteRegionError,
    pwaJobsFixtureNotice,
    pwaJobsFixtureSurface,
    pwaJobsPreviewJobs,
    pwaJobsSotActive,
    pwaRegionLabel,
    pwaSearchHasStatus,
    pwaSearchWithStatus,
    pwaStatusFromSearch,
    pwaWalkStatusFromSot,
    pwaWalkStatusHref,
    statusSurfaceState,
} from './ordersSurface';
import DemoWalk from './DemoWalk';
import RegionWalkSwitcher from './RegionWalkSwitcher';
import { pwaDemoWalkOpenTab, shouldResetPwaWalkResidue } from './demoWalk';
import {
    assignedJobIdsByStatus,
    assignedJobsQuery,
    jobsFromAssignedSnapshot,
    shouldCoalesceAssignedJobsFocusRefresh,
    shouldOpenActiveTab,
    shouldOpenCompleteTab,
    shouldRefreshAssignedJobsOnFocus,
} from './assignedJobs';

const OrdersPage = () => {
    const { techId, tech, jobsRef, user } = useContext(AuthContext);
    const location = useLocation();
    const history = useHistory();
    const sot = pwaJobsSotActive(location.search, { techId });
    const preview = pwaJobsFixtureSurface(location.search, { techId });
    const createSot = pwaCreateSotActive(location.search, { techId });
    const createPreview = pwaCreatePreviewSurface(location.search);
    const createGuard = createSot || createPreview;
    const trueEmptySot = pwaTrueEmptySotActive(location.search, { techId });
    const trueEmptyPreview = pwaTrueEmptyPreviewSurface(location.search);
    const trueEmptyWalk = pwaTrueEmptyWalkKind(trueEmptySot, trueEmptyPreview);
    const createFixture = pwaCreateFixtureKind(sot, preview, createSot, createPreview, trueEmptyWalk);
    const createFixtureSource = createSot || sot
        ? 'sot'
        : createPreview || preview
          ? 'preview'
          : trueEmptySot
            ? 'true-empty'
            : trueEmptyPreview
              ? 'true-empty-preview'
              : null;
    const skipLiveList = Boolean(preview || createGuard || trueEmptyWalk);
    const fixtureNotice =
        pwaTrueEmptyFixtureNotice(location.search, { techId }) ||
        pwaCreateFixtureNotice(location.search, { techId }) ||
        pwaJobsFixtureNotice(location.search, { techId });

    const [jobs, setJobs] = useState<any>([]);

    const [rejectedJobDetails, setRejectedJobDetails] = useState<any>([]);

    const claimRegionId = (user as { claims?: { regionId?: string } } | null)?.claims?.regionId || '';
    const fixtureWalk = isPwaWalkSot(sot) || isPwaWalkSot(preview);
    const walkRegionId = pwaDisplayRegionId(location.search, claimRegionId, fixtureWalk);
    const walkRegionLabel = pwaRegionLabel(walkRegionId);
    const liveWriteRegionError = fixtureWalk ? null : pwaLiveWriteRegionError(claimRegionId);
    const walkJobStatus = pwaStatusFromSearch(location.search) || pwaWalkStatusFromSot(location.search) || undefined;
    const [openTab, setOpenTab] = React.useState(pwaDemoWalkOpenTab({ sot, preview, search: location.search }));
    const previousSotRef = useRef<string | null>(sot);
    const previousCreateSotRef = useRef<string | null>(createSot);
    const previousWalkStatusRef = useRef(pwaStatusFromSearch(location.search));

    const jobsReadyRef = useRef(false);
    const lastFocusRefreshRef = useRef(0);
    const primedActiveRef = useRef(false);
    const primedCompleteRef = useRef(false);
    const previousActiveIdsRef = useRef<string[]>([]);
    const previousCompleteIdsRef = useRef<string[]>([]);

    const [filterTerm, setFilterTerm] = useState('');

    const [reloadToken, setReloadToken] = useState(0);

    const [jobsLoading, setJobsLoading] = useState(true);

    const [jobsError, setJobsError] = useState('');

    const [offline, setOffline] = useState(
        typeof navigator !== 'undefined' ? navigator.onLine === false : false,
    );

    const retry = useCallback(() => {
        if (!skipLiveList) {
            setOffline(typeof navigator !== 'undefined' ? navigator.onLine === false : false);
        }
        setReloadToken((token) => token + 1);
    }, [skipLiveList]);

    const clearDateFilter = useCallback(() => {
        setFilterTerm('');
    }, []);

    useEffect(() => {
        if (skipLiveList) return undefined;
        const goOffline = () => setOffline(true);
        const goOnline = () => setOffline(false);
        window.addEventListener('offline', goOffline);
        window.addEventListener('online', goOnline);
        return () => {
            window.removeEventListener('offline', goOffline);
            window.removeEventListener('online', goOnline);
        };
    }, [skipLiveList]);

    useEffect(() => {
        if (preview) {
            setJobs(pwaJobsPreviewJobs(preview));
            setJobsError(preview === 'error' ? pwaJobsFixtureError(sot ? 'sot' : 'preview') : '');
            setOffline(preview === 'offline');
            setJobsLoading(false);
            jobsReadyRef.current = preview !== 'error' && preview !== 'offline';
            return undefined;
        }
        if (createGuard || trueEmptyWalk) {
            setJobs([]);
            setJobsError('');
            setOffline(false);
            setJobsLoading(false);
            return undefined;
        }
        const companyId = companyIdFromClaims((user as { claims?: { companyId?: unknown } } | null)?.claims);
        if (!jobsRef || !techId || !companyId) {
            setJobs([]);
            setJobsError(
                jobsRef && techId && !companyId
                    ? MISSING_COMPANY_CLAIM_ERROR
                    : 'Assigned jobs are not available for this session.',
            );
            setJobsLoading(false);
            return undefined;
        }
        let cancelled = false;
        const query = assignedJobsQuery(jobsRef, techId, companyId) as {
            onSnapshot: (
                onNext: (snap: { forEach: (cb: (doc: { id: string; data: () => Record<string, unknown> }) => void) => void }) => void,
                onError: (error: { message?: string }) => void,
            ) => () => void;
            get?: (options?: { source?: string }) => Promise<{
                forEach: (cb: (doc: { id: string; data: () => Record<string, unknown> }) => void) => void;
            }>;
        };
        if (!jobsReadyRef.current) {
            setJobsLoading(true);
            setJobsError('');
        }
        const applySnapshot = (querySnapshot: {
            forEach: (cb: (doc: { id: string; data: () => Record<string, unknown> }) => void) => void;
        }) => {
            if (cancelled) return;
            const arrayJobs = jobsFromAssignedSnapshot(querySnapshot);
            arrayJobs.sort((a, b) => compareOrdersJobsByDate(a, b, false));
            setJobs(arrayJobs);
            setJobsError('');
            setJobsLoading(false);
            jobsReadyRef.current = true;
        };
        const unsubscribe = query.onSnapshot(applySnapshot, (error) => {
            if (cancelled) return;
            console.error('Unable to load assigned jobs', error);
            if (!jobsReadyRef.current) {
                setJobs([]);
                setJobsLoading(false);
            }
            setJobsError(rejectionMessage(error, 'Assigned jobs could not load.'));
        });
        const refreshFromServer = () => {
            if (cancelled || typeof query.get !== 'function') return;
            if (
                !shouldRefreshAssignedJobsOnFocus({
                    hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
                    ready: jobsReadyRef.current,
                    fixture: Boolean(preview),
                })
            ) {
                return;
            }
            const now = Date.now();
            if (!shouldCoalesceAssignedJobsFocusRefresh({ lastAt: lastFocusRefreshRef.current, now })) return;
            lastFocusRefreshRef.current = now;
            query
                .get({ source: 'server' })
                .then(applySnapshot)
                .catch((error: { message?: string }) => {
                    if (cancelled) return;
                    console.error('Unable to load assigned jobs', error);
                    setJobsError(rejectionMessage(error, 'Assigned jobs could not load.'));
                });
        };
        const onVisible = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            refreshFromServer();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            cancelled = true;
            unsubscribe();
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [techId, jobsRef, reloadToken, preview, sot, createGuard, trueEmptyWalk, user]);

    const [visibleModalType, setVisibleModalType] = useState('');
    const [activeDetailsId, setActiveDetailsId] = useState('');

    const [createJobModalVisibility, setCreateJobModalVisibility] = useState(false);

    useEffect(() => {
        if (
            shouldResetPwaWalkResidue({
                previousSot: previousSotRef.current,
                nextSot: sot,
                previousCreateSot: previousCreateSotRef.current,
                nextCreateSot: createSot,
            })
        ) {
            previousSotRef.current = sot;
            previousCreateSotRef.current = createSot;
            setOpenTab(pwaDemoWalkOpenTab({ sot, preview, search: location.search }));
            primedActiveRef.current = false;
            primedCompleteRef.current = false;
            previousActiveIdsRef.current = [];
            previousCompleteIdsRef.current = [];
        }
    }, [sot, preview, createSot, location.search]);

    useEffect(() => {
        const nextStatus = pwaStatusFromSearch(location.search);
        if (previousWalkStatusRef.current === nextStatus) return;
        previousWalkStatusRef.current = nextStatus;
        if (nextStatus === 'pending') setOpenTab(1);
        else if (nextStatus === 'active') setOpenTab(2);
        else if (nextStatus === 'complete') setOpenTab(3);
    }, [location.search]);

    useEffect(() => {
        if (!isPwaWalkSot(sot) && !isPwaWalkSot(preview)) return;
        const fromSot = pwaWalkStatusFromSot(location.search);
        if (!fromSot || pwaSearchHasStatus(location.search)) return;
        history.replace(`/app/orders${pwaSearchWithStatus(location.search, fromSot)}`);
    }, [sot, preview, location.search, history]);

    useEffect(() => {
        if (createGuard) {
            setCreateJobModalVisibility(true);
            return;
        }
        setCreateJobModalVisibility(false);
    }, [createGuard, sot, preview, trueEmptyWalk]);

    const [sortingOrder, setSortingOrder] = useState('');

    const sortJobs = () => {
        if (sortingOrder === 'descending') {
            setSortingOrder('ascending');
        } else {
            setSortingOrder('descending');
        }
    };

    const jobsSurface = statusSurfaceState({
        loading: jobsLoading,
        error: jobsError,
        offline,
        count: jobs.length,
    });

    const loadedDateKeys = useMemo(() => {
        const seen = new Set<string>();
        const keys: string[] = [];
        jobs.forEach((job) => {
            const key = ordersJobDateKey(job);
            if (!seen.has(key)) {
                seen.add(key);
                keys.push(key);
            }
        });
        return keys;
    }, [jobs]);

    const filterdJobs = jobs.filter((job) => ordersJobMatchesFilter(job, filterTerm));

    const sortedJobs = useMemo(() => {
        const jobsByDate = [...filterdJobs];
        jobsByDate.sort(function (a, b) {
            return compareOrdersJobsByDate(a, b, sortingOrder === 'descending');
        });
        return jobsByDate;
    }, [filterdJobs, sortingOrder]);

    const rejectedJobs = useMemo(() => tech?.rejectedJobs || [], [tech]);

    const activeJobs = useMemo(() => sortedJobs.filter((job) => job.status === 'active'), [sortedJobs]);

    const pendingJobs = useMemo(() => sortedJobs.filter((job) => job.status === 'pending'), [sortedJobs]);

    const completeAndCancelledJobs = useMemo(
        () => sortedJobs.filter((job) => job.status === 'complete' || job.status === 'cancelled'),
        [sortedJobs],
    );

    const pendingJobsByDate = useMemo(() => groupBy(pendingJobs, (job) => ordersJobDateKey(job)), [pendingJobs]);

    const activeJobsByDate = useMemo(() => groupBy(activeJobs, (job) => ordersJobDateKey(job)), [activeJobs]);

    const completeJobsByDate = useMemo(
        () => groupBy(completeAndCancelledJobs, (job) => ordersJobDateKey(job)),
        [completeAndCancelledJobs],
    );

    const activeJobsLength = useMemo(() => activeJobs.length, [activeJobs]);

    const showFilterEmpty =
        jobsSurface === 'ready' && Boolean(filterTerm.trim()) && filterdJobs.length === 0 && jobs.length > 0;

    // Rejected rows stay on Pending when the assigned list is empty (surface 'empty').
    // Count them once, skipping ids already listed as pending.
    const pendingIds = new Set(pendingJobs.map((job) => job.id));
    const rejectedOnPendingTab =
        !showFilterEmpty && (jobsSurface === 'ready' || jobsSurface === 'empty');
    const pendingBadgeCount =
        pendingJobs.length +
        (rejectedOnPendingTab
            ? rejectedJobs.filter((job) => !pendingIds.has(job?.id)).length
            : 0);

    const headerDetail = ordersHeaderDetail({
        surface: jobsSurface,
        pendingCount: pendingJobs.length,
        activeCount: activeJobs.length,
        completeCount: completeAndCancelledJobs.length,
        loadedCount: jobs.length,
        filterEmpty: showFilterEmpty,
    });

    useEffect(() => {
        const activeFixture = sot === 'handoff' || preview === 'handoff' ? 'handoff' : null;
        const completeFixture =
            sot === 'complete-handoff' || preview === 'complete-handoff' ? 'complete-handoff' : null;
        if (jobsLoading && !activeFixture && !completeFixture) return;
        const nextActiveIds = assignedJobIdsByStatus(jobs, 'active');
        if (
            shouldOpenActiveTab({
                previousActiveIds: previousActiveIdsRef.current,
                nextActiveIds,
                primed: primedActiveRef.current,
                fixture: activeFixture,
                currentTab: openTab,
            })
        ) {
            setOpenTab(2);
        }
        const nextCompleteIds = assignedJobIdsByStatus(jobs, 'complete');
        if (
            shouldOpenCompleteTab({
                previousCompleteIds: previousCompleteIdsRef.current,
                nextCompleteIds,
                primed: primedCompleteRef.current,
                fixture: completeFixture,
                currentTab: openTab,
            })
        ) {
            setOpenTab(3);
        }
        if (!jobsLoading) {
            primedActiveRef.current = true;
            previousActiveIdsRef.current = nextActiveIds;
            primedCompleteRef.current = true;
            previousCompleteIdsRef.current = nextCompleteIds;
        }
    }, [jobs, jobsLoading, sot, preview, openTab]);

    const emptyKind = ordersEmptyCopyKind({
        sot,
        preview,
        trueEmptyWalk,
    });
    const liveEmpty = jobsSurface === 'empty' && !createFixture;
    const listCopy =
        createGuard || (jobsSurface === 'empty' && rejectedJobs.length > 0)
            ? null
            : ordersListCopy(jobsSurface, emptyKind);

    const showJobsChrome =
        jobsSurface === 'ready' || (jobsSurface === 'empty' && rejectedJobs.length > 0);

    const pendingTabEmpty =
        openTab === 1 &&
        jobsSurface === 'ready' &&
        !showFilterEmpty &&
        !listCopy &&
        pendingJobs.length === 0 &&
        rejectedJobs.length === 0;

    const activeTabEmpty =
        openTab === 2 && jobsSurface === 'ready' && !showFilterEmpty && !listCopy && activeJobs.length === 0;

    const completeTabEmpty =
        openTab === 3 &&
        jobsSurface === 'ready' &&
        !showFilterEmpty &&
        !listCopy &&
        completeAndCancelledJobs.length === 0;

    const closeWorkDetailsModal = () => {
        setVisibleModalType('');
        setActiveDetailsId('');
        setRejectedJobDetails(null);
    };

    const goToAcceptedDetails = (jobDetailsId) => {
        setVisibleModalType('acceptedJob');
        setActiveDetailsId(jobDetailsId);
    };

    const goToRejectedDetails = (jobDetails) => {
        setVisibleModalType('rejectedJob');
        setRejectedJobDetails(jobDetails);
    };

    const openCreateJobModal = () => {
        setCreateJobModalVisibility(true);
    };

    const closeCreateJobModal = () => {
        setCreateJobModalVisibility(false);
    };

    const techUnavailable = tech?.status === 'unavailable';

    return (
        <div data-testid="orders-page" data-jobs-surface={jobsSurface}>
            <LoaderComponent showLoader={jobsSurface === 'loading'} />
            <div className="bg-white-600 py-4 text-center shadow-lg">
                <div className="my-auto pl-2.5 pr-2.5">
                    <p className="text-2xl text-gray-800 font-semibold">Your current jobs</p>
                    {headerDetail ? <p className="text-lg text-gray-800">{headerDetail}</p> : null}
                    {fixtureNotice ? (
                        <p className="text-sm text-gray-600 mt-2 px-4" data-testid="jobs-preview-notice">
                            {fixtureNotice}
                        </p>
                    ) : null}
                    {isPwaWalkSot(sot) || isPwaWalkSot(preview) ? (
                        <RegionWalkSwitcher
                            regionId={walkRegionId}
                            search={location.search}
                            jobStatus={walkJobStatus}
                        />
                    ) : null}
                    <DemoWalk
                        search={location.search}
                        openTab={openTab}
                        jobStatus={walkJobStatus}
                        sot={sot}
                        preview={preview}
                        regionLabel={walkRegionLabel}
                    />
                    {sot ? <JobsSotSwitcher active={sot} search={location.search} /> : null}
                    {trueEmptyWalk ? <TrueEmptySwitcher active="walk" /> : null}
                    {createGuard ? <CreateSotSwitcher active={createGuard} /> : null}
                </div>
            </div>
            <button
                disabled={techUnavailable}
                type="button"
                className={
                    techUnavailable
                        ? 'bg-gray-200 text-gray-400 font-bold py-2 px-4 w-72 w-full'
                        : 'bg-green-400 hover:bg-green-900 text-white font-bold py-2 px-4 w-72 w-full'
                }
                onClick={() => openCreateJobModal()}
            >
                CREATE JOB
            </button>
            {liveWriteRegionError ? (
                <p className="text-sm text-red-700 mt-2 px-4" data-testid="pwa-live-write-region">
                    {liveWriteRegionError}
                </p>
            ) : null}

            <div className="flex flex-wrap pl-2.5 pr-2.5 mt-2 mb-8">
                <div className="w-full">
                    {jobsSurface === 'ready' && jobsError ? (
                        <div
                            role="alert"
                            data-testid="jobs-refresh-error"
                            className="py-4 bg-red-600 w-full text-white text-center"
                        >
                            {jobsError}
                        </div>
                    ) : null}
                    {listCopy && !showJobsChrome ? (
                        <div className="flex flex-col items-center mt-4">
                            <StatusEmptyCard
                                copy={listCopy}
                                detail={jobsSurface === 'error' ? jobsError : undefined}
                                onAction={
                                    jobsSurface === 'empty' ? openCreateJobModal : retry
                                }
                            />
                        </div>
                    ) : null}
                    {showJobsChrome ? (
                    <>
                    <ul className=" mb-0 list-none pt-3 pb-4 grid grid-cols-3 gap-4" data-open-tab={openTab} data-testid="jobs-tabs" role="tablist">
                        <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                            <button
                                className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                                    openTab === 1 ? 'text-white bg-primaryTheme' : 'text-blue-600 bg-white'
                                }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpenTab(1);
                                    history.replace(pwaWalkStatusHref(location.search, 'pending'));
                                }}
                                type="button"
                            >
                                Pending
                            </button>
                            {pendingBadgeCount > 0 ? (
                                <div className="object-right-top top-0 right-0 absolute bg-yellow-400 -m-3	rounded-full h-8 w-8 flex items-center justify-center text-white z-50">
                                    {pendingBadgeCount}
                                </div>
                            ) : null}
                        </li>
                        <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                            <button
                                className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                                    openTab === 2 ? 'text-white bg-primaryTheme' : 'text-blue-600 bg-white'
                                }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpenTab(2);
                                    history.replace(pwaWalkStatusHref(location.search, 'active'));
                                }}
                                type="button"
                            >
                                Active
                            </button>
                            {jobsSurface === 'ready' && activeJobsLength > 0 ? (
                                <div className="object-right-top top-0 right-0 absolute bg-green-400 -m-3 rounded-full h-8 w-8 flex items-center justify-center text-white z-50">
                                    {activeJobsLength}
                                </div>
                            ) : null}
                        </li>
                        <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                            <button
                                className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                                    openTab === 3 ? 'text-white bg-primaryTheme' : 'text-blue-600 bg-white'
                                }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpenTab(3);
                                    history.replace(pwaWalkStatusHref(location.search, 'complete'));
                                }}
                                type="button"
                            >
                                Complete
                            </button>
                            {jobsSurface === 'ready' && completeAndCancelledJobs.length > 0 ? (
                                <div className="object-right-top top-0 right-0 absolute bg-primaryTheme -m-3	rounded-full h-8 w-8 flex items-center justify-center text-white z-50">
                                    {completeAndCancelledJobs.length}
                                </div>
                            ) : null}
                        </li>
                    </ul>
                    {jobsSurface === 'ready' ? (
                        <div className="text-center">
                            <div className="shadow flex mt-1">
                                <input
                                    aria-label="Filter by date"
                                    className="p-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-full"
                                    data-testid="orders-date-filter"
                                    list="orders-date-options"
                                    onChange={(event) => setFilterTerm(event.target.value)}
                                    placeholder="Filter by date..."
                                    type="text"
                                    value={filterTerm}
                                />
                                <datalist id="orders-date-options">
                                    {loadedDateKeys.map((date) => (
                                        <option key={date} value={date}>
                                            {date}
                                        </option>
                                    ))}
                                </datalist>
                                <button
                                    type="button"
                                    aria-label="Clear date"
                                    onClick={clearDateFilter}
                                    className="w-auto flex justify-end items-center p-2 bg-red-600"
                                >
                                    <FontAwesomeIcon className="text-2xl text-white" icon={faTrash} />
                                </button>
                                <button
                                    type="button"
                                    className="text-white font-bold p-2 capitalize flex flex-row bg-blue-500"
                                    onClick={() => {
                                        sortJobs();
                                    }}
                                >
                                    Sort{' '}
                                    {sortingOrder === 'ascending' ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6 d-inline-block"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 17l-4 4m0 0l-4-4m4 4V3"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 7l4-4m0 0l4 4m-4-4v18"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : null}
                    <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 rounded">
                        <div className="px-1 pb-5 flex-auto">
                            <div className="tab-content tab-space mt-4 flex flex-col items-center">
                                {showFilterEmpty ? (
                                    <StatusEmptyCard
                                        copy={ordersFilterEmptyCard(jobs.length)}
                                        onAction={clearDateFilter}
                                    />
                                ) : null}
                                <div className={openTab === 1 ? 'block w-full' : 'hidden'} id="pending">
                                    {pendingTabEmpty ? (
                                        <StatusEmptyCard
                                            copy={ORDERS_TAB_EMPTY.pending}
                                            onAction={filterTerm ? clearDateFilter : openCreateJobModal}
                                        />
                                    ) : null}
                                    {Object.entries(pendingJobsByDate).map(([date, datedJobs]) => {
                                        return (
                                            <div key={date}>
                                                <h2 className="mt-3 pl-1"> {date}</h2>
                                                {datedJobs.map((job) => (
                                                    <WorkOrderComponent
                                                        job={job}
                                                        key={job.id}
                                                        goToDetails={goToAcceptedDetails}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                    {showFilterEmpty
                                        ? null
                                        : rejectedJobs.map((job) => (
                                              <>
                                                  <h2 className="mt-3 pl-1"> Rejected</h2>
                                                  <WorkOrderComponent
                                                      job={job}
                                                      key={job.id}
                                                      goToDetails={() => goToRejectedDetails(job)}
                                                  />
                                              </>
                                          ))}
                                </div>
                                <div className={openTab === 2 ? 'block w-full' : 'hidden'} data-testid="orders-active-panel" id="active">
                                    {activeTabEmpty ? (
                                        <StatusEmptyCard
                                            copy={ORDERS_TAB_EMPTY.active}
                                            onAction={filterTerm ? clearDateFilter : openCreateJobModal}
                                        />
                                    ) : null}
                                    {Object.entries(activeJobsByDate).map(([date, datedJobs]) => {
                                        return (
                                            <div key={date}>
                                                <h2 className="mt-3 pl-1"> {date}</h2>
                                                {datedJobs.map((job) => (
                                                    <WorkOrderComponent
                                                        job={job}
                                                        key={job.id}
                                                        goToDetails={goToAcceptedDetails}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className={openTab === 3 ? 'block w-full' : 'hidden'} id="complete">
                                    {completeTabEmpty ? (
                                        <StatusEmptyCard
                                            copy={ORDERS_TAB_EMPTY.complete}
                                            onAction={filterTerm ? clearDateFilter : openCreateJobModal}
                                        />
                                    ) : null}
                                    {Object.entries(completeJobsByDate).map(([date, datedJobs]) => {
                                        return (
                                            <div key={date}>
                                                <h2 className="mt-3 pl-1"> {date}</h2>
                                                {datedJobs.map((job) => (
                                                    <WorkOrderComponent
                                                        job={job}
                                                        key={job.id}
                                                        goToDetails={goToAcceptedDetails}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    </>
                    ) : null}
                </div>
            </div>
            {visibleModalType === 'acceptedJob' ? (
                <>
                    <AcceptedJobModal onClose={closeWorkDetailsModal} jobDetailsId={activeDetailsId} />
                </>
            ) : null}
            {visibleModalType === 'rejectedJob' ? (
                <>
                    <RejectedJobModal onClose={closeWorkDetailsModal} rejectedJob={rejectedJobDetails} />
                </>
            ) : null}
            {createJobModalVisibility && (
                <CreateJobModal
                    key={createGuard || 'live'}
                    fixture={createFixture}
                    fixtureSource={createFixtureSource}
                    liveEmpty={liveEmpty}
                    offline={offline}
                    onClose={closeCreateJobModal}
                />
            )}
        </div>
    );
};

export default OrdersPage;
